<template>
  <div>
    <div class="container-fluid">
      <div class="page-header">
        <Breadcrumbs main="Super Admin" title="Subscribers" />
      </div>

      <div class="card">
        <!-- Barre d'outils : recherche, filtre statut, export -->
        <div class="card-header">
          <div class="d-flex flex-wrap justify-content-between align-items-center">
            <h5 class="mb-0">
              Subscribers <small class="text-muted">({{ total }})</small>
            </h5>
            <div class="d-flex flex-wrap align-items-center mt-2 mt-md-0">
              <b-form-input
                v-model="searchQuery"
                size="sm"
                type="search"
                placeholder="Search email or name..."
                style="max-width: 240px;"
                class="mr-2"
                @keyup.enter="applyFilters"
              ></b-form-input>
              <b-form-select
                v-model="statusFilter"
                :options="statusOptions"
                size="sm"
                class="w-auto mr-2"
                @change="applyFilters"
              ></b-form-select>
              <button type="button" class="btn btn-light btn-sm mr-2" @click="applyFilters">
                <feather type="search" size="14" class="mr-1"></feather>Search
              </button>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                :disabled="loading || !total"
                @click="exportCsv"
              >
                <feather type="download" size="14" class="mr-1"></feather>Export CSV
              </button>
            </div>
          </div>
          <p class="text-danger mb-0 mt-2" v-if="actionError">{{ actionError }}</p>
        </div>

        <div class="card-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="text-muted mt-3 mb-0">Loading subscribers...</p>
          </div>
          <div v-else-if="loadError" class="text-center py-5">
            <p class="text-danger mb-0">{{ loadError }}</p>
            <button class="btn btn-outline-primary btn-sm mt-3" @click="load">Retry</button>
          </div>
          <template v-else>
            <div class="table-responsive datatable-vue text-center">
              <b-table
                show-empty
                striped
                hover
                head-variant="light"
                bordered
                stacked="md"
                :items="users"
                :fields="fields"
              >
                <template #empty><span>No subscriber matches these filters.</span></template>
                <template #cell(email)="field">
                  <a :href="'mailto:' + field.item.email">{{ field.item.email }}</a>
                </template>
                <template #cell(displayName)="field">{{ field.item.displayName || "—" }}</template>
                <template #cell(role)="field"><span class="text-capitalize">{{ field.item.role }}</span></template>
                <template #cell(status)="field">
                  <span :class="field.item.status ? 'badge badge-success' : 'badge badge-secondary'">
                    {{ field.item.status ? "Active" : "Disabled" }}
                  </span>
                </template>
                <template #cell(createdAt)="field">{{ formatDate(field.item.createdAt) }}</template>
                <template #cell(actions)="field">
                  <div class="row-actions" style="justify-content:center">
                    <button
                      type="button"
                      class="icon-btn"
                      :title="field.item.status ? 'Deactivate' : 'Activate'"
                      @click="toggleStatus(field.item)"
                    >
                      <feather :type="field.item.status ? 'user-x' : 'user-check'" size="16"></feather>
                    </button>
                    <button type="button" class="icon-btn" title="Offer / change plan" @click="openGrant(field.item)">
                      <feather type="gift" size="16"></feather>
                    </button>
                    <button type="button" class="icon-btn" title="Reset password" @click="openPassword(field.item)">
                      <feather type="key" size="16"></feather>
                    </button>
                    <button type="button" class="icon-btn icon-btn-danger" title="Delete account" @click="confirmDelete(field.item)">
                      <feather type="trash-2" size="16"></feather>
                    </button>
                  </div>
                </template>
              </b-table>
            </div>
            <b-col md="12" class="my-1 p-0 pagination-justify" v-if="total > limit">
              <b-pagination
                :value="currentPage"
                :total-rows="total"
                :per-page="limit"
                @input="onPageChange"
                class="mt-4"
              ></b-pagination>
            </b-col>
          </template>
        </div>
      </div>
    </div>

    <!-- Offrir / changer l'abonnement -->
    <b-modal ref="grantModal" title="Offer / change subscription" @ok="submitGrant" :ok-disabled="saving">
      <b-form @submit.prevent v-if="selected">
        <p class="text-muted small mb-3">Account: <strong>{{ selected.email }}</strong></p>
        <b-form-group label="Plan" label-for="grant-plan" class="mb-2">
          <b-form-select id="grant-plan" v-model="grantPlanId" :options="planOptions"></b-form-select>
        </b-form-group>
        <b-form-group label="Status" label-for="grant-status" class="mb-2">
          <b-form-select id="grant-status" v-model="grantStatus" :options="grantStatusOptions"></b-form-select>
        </b-form-group>
        <b-form-group label="Current period end (optional)" label-for="grant-period" class="mb-2">
          <b-form-input id="grant-period" v-model="grantPeriodEnd" type="date"></b-form-input>
        </b-form-group>
        <p class="text-danger mb-0" v-if="modalError">{{ modalError }}</p>
      </b-form>
    </b-modal>

    <!-- Réinitialiser le mot de passe -->
    <b-modal ref="passwordModal" title="Reset password" @ok="submitPassword" :ok-disabled="saving">
      <b-form @submit.prevent v-if="selected">
        <p class="text-muted small mb-3">Account: <strong>{{ selected.email }}</strong></p>
        <b-form-group label="New password" label-for="new-password" class="mb-2">
          <b-form-input id="new-password" v-model="newPassword" type="text" placeholder="At least 8 characters"></b-form-input>
        </b-form-group>
        <p class="text-danger mb-0" v-if="modalError">{{ modalError }}</p>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import firebase from "@/utils/firestore-shim";
import { downloadCSV } from "@/utils/store-stats";

export default {
  name: "AdminSubscribers",
  data() {
    return {
      fields: [
        { key: "email", label: "Email" },
        { key: "displayName", label: "Name" },
        { key: "role", label: "Role" },
        { key: "storeCount", label: "Stores" },
        { key: "status", label: "Status" },
        { key: "createdAt", label: "Created" },
        { key: "actions", label: "" }
      ],
      statusOptions: [
        { value: "", text: "All statuses" },
        { value: "1", text: "Active" },
        { value: "0", text: "Disabled" }
      ],
      grantStatusOptions: [
        { value: "active", text: "Active" },
        { value: "trialing", text: "Trialing" },
        { value: "past_due", text: "Past due" },
        { value: "canceled", text: "Canceled" },
        { value: "incomplete", text: "Incomplete" }
      ],
      users: [],
      plans: [],
      total: 0,
      limit: 25,
      offset: 0,
      currentPage: 1,
      searchQuery: "",
      statusFilter: "",
      loading: false,
      loadError: "",
      actionError: "",
      modalError: "",
      saving: false,
      selected: null,
      grantPlanId: "",
      grantStatus: "active",
      grantPeriodEnd: "",
      newPassword: ""
    };
  },
  computed: {
    planOptions() {
      return this.plans.map((p) => ({
        value: p.id,
        text: p.name + " — " + this.money(p) +
          (p.maxStores === -1 ? " · unlimited stores" : " · " + p.maxStores + " store(s)")
      }));
    }
  },
  mounted() {
    this.loadPlans();
    this.load();
  },
  methods: {
    money(plan) {
      const symbol = String(plan.currency || "usd").toUpperCase() === "EUR" ? "€" : "$";
      return symbol + Number(plan.priceMonthly || 0).toFixed(2);
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
    },
    queryString(extra) {
      const params = [];
      if (this.searchQuery.trim()) params.push("q=" + encodeURIComponent(this.searchQuery.trim()));
      if (this.statusFilter) params.push("status=" + this.statusFilter);
      params.push("limit=" + (extra && extra.limit ? extra.limit : this.limit));
      params.push("offset=" + (extra && extra.offset !== undefined ? extra.offset : this.offset));
      return params.join("&");
    },
    async loadPlans() {
      try {
        const res = await firebase.api.get("/admin/plans");
        this.plans = (res && res.plans) || [];
      } catch (e) {
        this.plans = [];
      }
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      this.actionError = "";
      try {
        const res = await firebase.api.get("/admin/users?" + this.queryString());
        this.users = (res && res.users) || [];
        this.total = (res && res.total) || 0;
      } catch (e) {
        this.loadError = "Could not load subscribers: " + ((e && e.message) || e);
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.offset = 0;
      this.load();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.offset = (page - 1) * this.limit;
      this.load();
    },
    toast(message, type) {
      if (this.$toasted) {
        this.$toasted.show(message, { theme: "bubble", position: "bottom-right", type: type || "info", duration: 3000 });
      }
    },
    async toggleStatus(user) {
      this.actionError = "";
      const next = !user.status;
      try {
        await firebase.api.patch("/admin/users/" + user.id, { status: next });
        user.status = next;
        this.toast(next ? "Account activated." : "Account deactivated.", "success");
      } catch (e) {
        this.actionError = "Unable to update status: " + ((e && e.message) || e);
      }
    },
    openGrant(user) {
      this.selected = user;
      this.modalError = "";
      this.grantPlanId = this.plans.length ? this.plans[0].id : "";
      this.grantStatus = "active";
      this.grantPeriodEnd = "";
      if (!this.plans.length) this.loadPlans();
      this.$refs.grantModal.show();
    },
    async submitGrant(event) {
      if (!this.grantPlanId) {
        this.modalError = "Choose a plan.";
        event.preventDefault();
        return;
      }
      this.saving = true;
      this.modalError = "";
      try {
        const body = { planId: this.grantPlanId, status: this.grantStatus };
        if (this.grantPeriodEnd) body.currentPeriodEnd = new Date(this.grantPeriodEnd).toISOString();
        await firebase.api.post("/admin/users/" + this.selected.id + "/subscription", body);
        this.toast("Subscription updated.", "success");
        await this.load();
      } catch (e) {
        this.modalError = "Unable to update subscription: " + ((e && e.message) || e);
        event.preventDefault();
      } finally {
        this.saving = false;
      }
    },
    openPassword(user) {
      this.selected = user;
      this.modalError = "";
      this.newPassword = "";
      this.$refs.passwordModal.show();
    },
    async submitPassword(event) {
      if (!this.newPassword || this.newPassword.length < 8) {
        this.modalError = "Password must be at least 8 characters.";
        event.preventDefault();
        return;
      }
      this.saving = true;
      this.modalError = "";
      try {
        await firebase.api.post("/admin/users/" + this.selected.id + "/password", { newPassword: this.newPassword });
        this.toast("Password reset.", "success");
      } catch (e) {
        this.modalError = "Unable to reset password: " + ((e && e.message) || e);
        event.preventDefault();
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(user) {
      this.$bvModal
        .msgBoxConfirm("Delete the account " + user.email + " ? This removes its subscriptions and store links (stores themselves are kept).", {
          title: "Confirmation",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "DELETE",
          cancelTitle: "CANCEL",
          footerClass: "p-2"
        })
        .then((value) => {
          if (value) this.deleteUser(user);
        })
        .catch(() => {});
    },
    async deleteUser(user) {
      this.actionError = "";
      try {
        await firebase.api.request("DELETE", "/admin/users/" + user.id);
        this.toast("Account deleted.", "success");
        await this.load();
      } catch (e) {
        this.actionError = "Unable to delete account: " + ((e && e.message) || e);
      }
    },
    async exportCsv() {
      try {
        const res = await firebase.api.get("/admin/users?" + this.queryString({ limit: 10000, offset: 0 }));
        const rows = ((res && res.users) || []).map((u) => ({
          Email: u.email,
          Name: u.displayName || "",
          Role: u.role || "",
          Stores: u.storeCount || 0,
          Status: u.status ? "Active" : "Disabled",
          Created: u.createdAt || ""
        }));
        downloadCSV(rows, "subscribers-" + new Date().toISOString().slice(0, 10) + ".csv");
      } catch (e) {
        this.actionError = "Unable to export: " + ((e && e.message) || e);
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
